<template>
  <RModal
    ref="modal"
    :persistent="!changesHappened"
    full-size
    title="Manage Price Adjustments"
    no-footer
    control-close-icon-on-parent
    @close-icon="onCloseIcon"
  >
    <CreateItemModal v-if="period" ref="createItemModal" :period-id="period.id" @created="addNewItem" />

    <div v-if="period" class="modal-content q-py-md">
      <RCard>
        <AgGrid
          :options="gridOptions"
          :row-data="tableRows"
          @ready="agGrid = $event"
          @cell-value-changed="cellValueChanged"
          @model-updated="modelUpdated"
        >
          <template #header>
            <Grid gaf="column" justify-content="space-between" align-items="center">
              <div class="text-subtitle-1 text-secondary-2 q-ml-sm q-mt-sm">
                Subscription period: {{ formatDateForCustomDisplay(period.start_date) }} -
                {{ formatDateForCustomDisplay(period.end_date) || 'N/A' }}
              </div>
              <Grid gaf="column" gap="16px" align-items="center">
                <RBtn type="text" size="small" @click="$refs.createItemModal.show()">Create new item</RBtn>
                <ShowHideCols :ag-grid="agGrid" />
              </Grid>
            </Grid>
          </template>
        </AgGrid>
      </RCard>
    </div>
  </RModal>
</template>

<script>
import * as apiTo from '@/modular-api'
// Libraries
import { cloneDeep } from 'lodash'
// Mixins
import updateMethods from '../updateMethods'
import { gridOptions } from './grid-options'
// Components
import AgGrid from '@/ag-grid/AgGrid'
import ShowHideCols from '@/ag-grid/components/show-hide-cols/ShowHideCols'
import RModal from '@/ui/RModal'
import CreateItemModal from './CreateItemModal.vue'

export default {
  components: {
    AgGrid,
    ShowHideCols,
    RModal,
    CreateItemModal
  },

  mixins: [gridOptions, updateMethods],

  data: () => ({
    period: null,
    items: null,
    agGrid: null,
    changesHappened: false
  }),

  computed: {
    dynamicItemsExist() {
      if (!this.items) throw new Error('dynamicItemsExist should be accessed after items are loaded')

      return !!this.items.length
    },

    tableRows() {
      if (!this.items) return null

      if (this.dynamicItemsExist) return this.items
      return [
        {
          is_static: true,
          id: this.period.id,
          description: this.defaultDescription,
          price: this.period.price,
          exchange_rate: this.period.exchange_rate,
          normalized_price: this.period.normalized_price,
          subscription_period: cloneDeep(this.period)
        }
      ]
    }
  },

  created() {
    this.defaultDescription = 'Original price'
  },

  methods: {
    show(period) {
      if (period.id !== this.period?.id) this.items = null

      this.period = cloneDeep(period)
      this.getAndSetItems()
      this.$refs.modal.show()
    },

    hide() {
      this.$refs.modal.hide()
      this.changesHappened = false
    },

    async onCloseIcon() {
      if (this.changesHappened) {
        if (this.dynamicItemsExist) await this.updateSubscriptionPeriods()
        else this.$store.commit('subscription-show/updatePeriod', this.period)
      }

      this.hide()
    },

    async updateSubscriptionPeriods() {
      this.$refs.modal.setLoading()

      await Promise.all([
        this.$store.dispatch('subscription-show/getAndSetSubscriptionPeriods', this.$route.params.id),
        this.$store.dispatch('subscription-show/fetchSubscription', this.$route.params.id)
      ])

      this.$refs.modal.setLoading(false)
    },

    getAndSetItems() {
      apiTo.GET_SUBSCRIPTION_PERIOD_ITEMS(this.period.id).then(({ data }) => (this.items = data.result))
    },

    addNewItem(newItem) {
      this.changesHappened = true
      if (this.dynamicItemsExist) this.agGrid.api.applyTransaction({ add: [newItem] })
      else this.getAndSetItems()
    },

    deleteItem(itemId) {
      this.changesHappened = true
      apiTo.DELETE_SUBSCRIPTION_PERIOD_ITEM(this.period.id, itemId).then(() => {
        this.agGrid.api.applyTransaction({ remove: [{ id: itemId }] })
      })
    },

    cellValueChanged(event) {
      this.changesHappened = true
      if (event.data.is_static) this.updateStaticRow(event)
      else this.updateDynamicRow(event)
    },

    updateStaticRow(event) {
      this.um_update({
        column: event.column,
        oldValue: event.oldValue,
        newValue: event.newValue,
        data: { ...cloneDeep(event.data.subscription_period), [event.column.colId]: event.newValue }
      })
    },

    updateDynamicRow(event) {
      if (!this.um_didValueChanged(event.oldValue, event.newValue)) return

      const column = event.column.colId

      apiTo
        .MODIFY_SUBSCRIPTION_PERIOD_ITEM(this.period.id, event.data.id, { [column]: event.newValue })
        .then(({ data }) => this.agGrid.api.applyTransaction({ update: [data.result] }))
        .catch(() => this.agGrid.api.applyTransaction({ update: [{ ...event.data, [column]: event.oldValue }] }))
    },

    refreshData(updatedPeriod) {
      this.period = updatedPeriod
    },

    modelUpdated(event) {
      if (!this.items) return

      event.api.setPinnedBottomRowData([this.generatePinnedBottomRow()])
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';

.modal-content {
  @include table-with-no-filters;
}
</style>
